// src/components/tasks/TaskCreateDialog.tsx
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Badge } from "@/components/ui/badge"
import { Paperclip, X } from "lucide-react"
import { useState } from "react"
import { toast } from "@/hooks/use-toast"
import { useAuthStore } from "@/store/authStore"
import { db, storage } from "@/lib/firebase"
import { addDoc, collection, serverTimestamp, updateDoc, doc, arrayUnion } from "firebase/firestore"
import { uploadBytesResumable, getDownloadURL, ref as storageRef } from "firebase/storage"
import { useCalendarEventStore } from "@/store/calendarEventStore"

// === ДИЗАЙН-СЛОЙ ===
import { taskCreateTheme } from "./ui/TaskCreateDialogUI"

const employees = [
  { id: 1, name: "Андреев Алексей Евгеньевич" },
  { id: 2, name: "Багаутдинов Ахмет Айратович" },
  { id: 3, name: "Барладян Ирина Алексеевна" },
  { id: 4, name: "Павлик Кристина Валентиновна" },
  { id: 5, name: "Султанова Диана Рашитовна" },
  { id: 6, name: "Казарин Сергей Константинович" },
  { id: 7, name: "Степанушкин Сергей Вадимович" },
]

export default function TaskCreateDialog() {
  const { user } = useAuthStore()
  const { addEvent } = useCalendarEventStore()

  const [open, setOpen] = useState(false)
  const [newTask, setNewTask] = useState({
    title: "",
    description: "",
    assigneeId: 0,
    coAssigneeIds: [] as number[],
    observerIds: [] as number[],
    deadline: "",
    urgency: "planned" as "urgent" | "planned",
    difficulty: "medium" as "easy" | "medium" | "hard",
    reminderInterval: "",
  })
  const [newTaskFile, setNewTaskFile] = useState<File | null>(null)

  const userId = user ? Number(user.id) : 0

  const canAssignTo = (assigneeId: number): boolean => {
    if (assigneeId === userId) return true
    if (userId === 1) return true
    if (userId === 2) return assigneeId !== 1
    if (userId === 3 || userId === 4) return [3, 4, 5, 6, 7].includes(assigneeId)
    return false
  }

  const uploadFileAndGetData = async (file: File | null) => {
    if (!file) return null
    try {
      const fileRef = storageRef(storage, `tasks/${Date.now()}_${file.name}`)
      const uploadTask = uploadBytesResumable(fileRef, file)
      await new Promise((resolve, reject) => {
        uploadTask.on("state_changed", null, reject, resolve)
      })
      const url = await getDownloadURL(uploadTask.snapshot.ref)
      return { name: file.name, url }
    } catch (error) {
      toast({ title: "Ошибка загрузки файла", variant: "destructive" })
      throw error
    }
  }

  const createTask = async () => {
    if (!user || !newTask.title || newTask.assigneeId === 0 || !newTask.deadline) {
      toast({ title: "Ошибка", description: "Заполните все обязательные поля", variant: "destructive" })
      return
    }

    if (!canAssignTo(newTask.assigneeId)) {
      toast({ title: "Ошибка", description: "Вы не можете ставить задачу этому сотруднику", variant: "destructive" })
      return
    }

    if (newTask.coAssigneeIds?.length > 0) {
      for (const coId of newTask.coAssigneeIds) {
        if (!canAssignTo(coId)) {
          toast({ title: "Ошибка", description: "Вы не можете назначить этого соисполнителя", variant: "destructive" })
          return
        }
        if (coId === newTask.assigneeId) {
          toast({ title: "Ошибка", description: "Соисполнитель не может быть тем же, что и исполнитель", variant: "destructive" })
          return
        }
      }
    }

    if (newTask.observerIds?.length > 0) {
      for (const obsId of newTask.observerIds) {
        if (obsId === userId) {
          toast({ title: "Ошибка", description: "Нельзя добавить себя в наблюдатели", variant: "destructive" })
          return
        }
      }
    }

    const fileData = await uploadFileAndGetData(newTaskFile)

    const coAssignees = newTask.coAssigneeIds
      .map((id) => employees.find((e) => e.id === id)?.name || "")
      .filter(Boolean)

    const observers = newTask.observerIds
      .map((id) => employees.find((e) => e.id === id)?.name || "")
      .filter(Boolean)

    const isSelfTask = newTask.assigneeId === userId

    try {
      const taskRef = await addDoc(collection(db, "tasks"), {
        title: newTask.title,
        description: newTask.description,
        assigneeId: newTask.assigneeId,
        assignee: employees.find((e) => e.id === newTask.assigneeId)?.name || "",
        coAssigneeIds: newTask.coAssigneeIds || [],
        coAssignees,
        observerIds: newTask.observerIds || [],
        observers,
        urgency: newTask.urgency,
        difficulty: newTask.difficulty,
        reminderInterval: newTask.reminderInterval,
        deadline: newTask.deadline,
        status: "new",
        approvalStatus: "new",
        approvalCount: 0,
        file: fileData,
        acceptedByExecutor: false,
        approvedBy: [],
        approvedByCreator: false,
        isBlock: false,
        createdAt: serverTimestamp(),
        createdBy: user.name,
        createdById: user.id,
        isSelfTask,
        history: [{ userId: user.id, userName: user.name, action: "created", date: new Date().toISOString() }],
        messages: [],
      })

      await updateDoc(doc(db, "tasks", taskRef.id), {
        unreadBy: arrayUnion(newTask.assigneeId, ...newTask.coAssigneeIds, ...newTask.observerIds)
      })

      toast({ title: isSelfTask ? "Своя задача создана!" : "Задача создана!" })

      if (newTask.deadline) {
        try {
          await addEvent({
            date: newTask.deadline,
            title: newTask.title,
            type: "task",
            description: newTask.description || "",
            createdBy: user.id.toString(),
            sourceTaskId: taskRef.id,
          })
          toast({ title: "Задача добавлена в календарь" })
        } catch (calendarErr) {
          console.error("Ошибка добавления в календарь:", calendarErr)
        }
      }

      setOpen(false)
      setNewTask({ title: "", description: "", assigneeId: 0, coAssigneeIds: [], observerIds: [], deadline: "", urgency: "planned", difficulty: "medium", reminderInterval: "" })
      setNewTaskFile(null)
    } catch (error) {
      console.error("Ошибка создания задачи:", error)
      toast({ title: "Ошибка", description: "Не удалось создать задачу", variant: "destructive" })
    }
  }

  const addCoAssignee = (coId: number) => {
    if (!newTask.coAssigneeIds.includes(coId) && coId !== newTask.assigneeId) {
      setNewTask({ ...newTask, coAssigneeIds: [...newTask.coAssigneeIds, coId] })
    }
  }

  const removeCoAssignee = (coId: number) => {
    setNewTask({ ...newTask, coAssigneeIds: newTask.coAssigneeIds.filter(id => id !== coId) })
  }

  const addObserver = (obsId: number) => {
    if (!newTask.observerIds.includes(obsId) && obsId !== userId) {
      setNewTask({ ...newTask, observerIds: [...newTask.observerIds, obsId] })
    }
  }

  const removeObserver = (obsId: number) => {
    setNewTask({ ...newTask, observerIds: newTask.observerIds.filter(id => id !== obsId) })
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className={taskCreateTheme.triggerButton}>
          + Поставить задачу
        </Button>
      </DialogTrigger>

      <DialogContent className={taskCreateTheme.dialogContent}>
        <DialogHeader>
          <DialogTitle className="text-2xl">Новая задача</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label>Наименование задачи</Label>
            <Input
              value={newTask.title}
              onChange={(e) => setNewTask({ ...newTask, title: e.target.value })}
              className={taskCreateTheme.formInput}
            />
          </div>

          <div>
            <Label>Подробное описание</Label>
            <Textarea
              value={newTask.description}
              onChange={(e) => setNewTask({ ...newTask, description: e.target.value })}
              className={taskCreateTheme.formTextarea}
              rows={4}
            />
          </div>

          <div>
            <Label>Исполнитель</Label>
            <Select onValueChange={(v) => setNewTask({ ...newTask, assigneeId: +v })}>
              <SelectTrigger className={taskCreateTheme.selectTrigger}>
                <SelectValue placeholder="Выберите сотрудника" />
              </SelectTrigger>
              <SelectContent className={taskCreateTheme.selectContent}>
                {employees.filter((e) => canAssignTo(e.id)).map((e) => (
                  <SelectItem key={e.id} value={e.id.toString()} className="hover:bg-purple-800/60 text-purple-200">
                    {e.name}
                    {e.id === userId && <span className="ml-2 text-emerald-400 text-xs font-medium">(себе)</span>}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Соисполнители */}
          <div>
            <Label>Соисполнители (не обязательно)</Label>
            <div className="flex flex-wrap gap-2 mb-2">
              {newTask.coAssigneeIds.map((coId) => {
                const emp = employees.find(e => e.id === coId)
                return (
                  <Badge key={coId} className={taskCreateTheme.badgeCoAssignee}>
                    {emp?.name}
                    <button onClick={() => removeCoAssignee(coId)} className="ml-2">
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                )
              })}
            </div>
            <Select onValueChange={(v) => addCoAssignee(+v)}>
              <SelectTrigger className={taskCreateTheme.selectTrigger}>
                <SelectValue placeholder="Добавить соисполнителя" />
              </SelectTrigger>
              <SelectContent className={taskCreateTheme.selectContent}>
                {employees
                  .filter((e) => canAssignTo(e.id) && e.id !== newTask.assigneeId && !newTask.coAssigneeIds.includes(e.id))
                  .map((e) => (
                    <SelectItem key={e.id} value={e.id.toString()} className="hover:bg-purple-800/60 text-purple-200">
                      {e.name}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>

          {/* Наблюдатели */}
          <div>
            <Label>Наблюдатели (не обязательно)</Label>
            <div className="flex flex-wrap gap-2 mb-2">
              {newTask.observerIds.map((obsId) => {
                const emp = employees.find(e => e.id === obsId)
                return (
                  <Badge key={obsId} variant="secondary" className={taskCreateTheme.badgeObserver}>
                    {emp?.name}
                    <button onClick={() => removeObserver(obsId)} className="ml-2">
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                )
              })}
            </div>
            <Select onValueChange={(v) => addObserver(+v)}>
              <SelectTrigger className={taskCreateTheme.selectTrigger}>
                <SelectValue placeholder="Добавить наблюдателя" />
              </SelectTrigger>
              <SelectContent className={taskCreateTheme.selectContent}>
                {employees
                  .filter((e) => e.id !== userId && !newTask.observerIds.includes(e.id))
                  .map((e) => (
                    <SelectItem key={e.id} value={e.id.toString()} className="hover:bg-purple-800/60 text-purple-200">
                      {e.name}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Срочность</Label>
              <Select onValueChange={(v) => setNewTask({ ...newTask, urgency: v as "urgent" | "planned" })}>
                <SelectTrigger className={taskCreateTheme.selectTrigger}>
                  <SelectValue placeholder="Планово" />
                </SelectTrigger>
                <SelectContent className={taskCreateTheme.selectContent}>
                  <SelectItem value="planned">Планово</SelectItem>
                  <SelectItem value="urgent">Срочно</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Уровень сложности</Label>
              <Select onValueChange={(v) => setNewTask({ ...newTask, difficulty: v as "easy" | "medium" | "hard" })}>
                <SelectTrigger className={taskCreateTheme.selectTrigger}>
                  <SelectValue placeholder="Средняя" />
                </SelectTrigger>
                <SelectContent className={taskCreateTheme.selectContent}>
                  <SelectItem value="easy">Легкая</SelectItem>
                  <SelectItem value="medium">Средняя</SelectItem>
                  <SelectItem value="hard">Сложная</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label>Напоминание</Label>
            <Input
              value={newTask.reminderInterval}
              onChange={(e) => setNewTask({ ...newTask, reminderInterval: e.target.value })}
              placeholder="например: 1 день до дедлайна"
              className={taskCreateTheme.formInput}
            />
          </div>

          <div>
            <Label>Срок исполнения (дедлайн)</Label>
            <Input
              type="date"
              value={newTask.deadline}
              onChange={(e) => setNewTask({ ...newTask, deadline: e.target.value })}
              className={taskCreateTheme.formInput}
            />
          </div>

          <div>
            <Label>Прикрепить файл</Label>
            <label className="cursor-pointer block">
              <input
                type="file"
                className="hidden"
                onChange={(e) => setNewTaskFile(e.target.files?.[0] || null)}
              />
              <div className={taskCreateTheme.fileUploadArea}>
                <Paperclip className="h-5 w-5" />
                <span className="text-sm">
                  {newTaskFile ? newTaskFile.name : "Выбрать файл"}
                </span>
              </div>
            </label>
            {newTaskFile && (
              <button onClick={() => setNewTaskFile(null)} className="text-red-400 text-sm mt-2 block">
                Удалить файл
              </button>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button onClick={createTask} className={taskCreateTheme.createButton}>
            Создать задачу
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}